/**
 * Integrytas Group - Base App (MOBILE)
 * Page: EDS.OPT.OUT — mobile-native.
 *
 * Touch-first counterpart of src/pages/eds/EdsOptOutPage.tsx. A DISTINCT
 * presentation (dense card list + scope chips + bottom-sheet detail) over the
 * SAME data/logic layer: reads via apiDb (eds_opt_out joined to eds_recipient
 * for the email and eds_recipient_list + _tl for the scoped list name; reason /
 * method codes resolved to localized names from their _tl lookups, §17.B4), and
 * the one write — Reinstate — routes through the Common Routine (routedUpdate),
 * §2. Never the desktop grid re-hosted; §16.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, MailX, MailCheck, RotateCcw, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { hasAccess, getForensicActorId } from '../../types/auth';
import { formatDate, formatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobilePillTone,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { MobilePageComponentProps } from '../mobile_page_components';

const PAGE_CODE = 'EDS.OPT.OUT';

const PILL_TONE_KEY: Record<string, MobilePillTone> = {
  'm-p-ok': 'ok', 'm-p-info': 'info', 'm-p-wait': 'wait', 'm-p-no': 'no', 'm-p-you': 'you',
};

interface MobileOptOutRow {
  opt_out_id: string;
  recipient_id: string;
  list_id: string | null;
  reason_code: string;
  method_code: string;
  opted_out_at: string;
  reinstated_at: string | null;
  notes: string | null;
  email_address: string;
  list_name: string | null;
}

type ScopeFacet = 'all' | 'global' | 'list';

// EDS translation tables key on a text language_id (§8), not locale_code.
interface TlTextRow {
  language_id: string;
  display_text: string;
}

// Opt-out method -> status-pill variant (label stays localized from _tl).
const METHOD_PILL: Record<string, string> = {
  unsubscribe_link: 'm-p-info',
  manual: 'm-p-wait',
  spam_complaint: 'm-p-no',
  bounce_auto: 'm-p-wait',
};

function resolveTl(tls: TlTextRow[], locale: string): string | undefined {
  const exact = tls.find((tl) => tl.language_id === locale);
  const fb = tls.find((tl) => tl.language_id === 'EN_US') ?? tls[0];
  return (exact ?? fb)?.display_text;
}

function buildLabelMap(
  rows: { code: string; language_id: string; display_text: string }[],
  locale: string,
): Record<string, string> {
  const byCode: Record<string, TlTextRow[]> = {};
  for (const r of rows) (byCode[r.code] ??= []).push({ language_id: r.language_id, display_text: r.display_text });
  const out: Record<string, string> = {};
  for (const [code, tls] of Object.entries(byCode)) out[code] = resolveTl(tls, locale) ?? code;
  return out;
}

export function MobileEdsOptOutPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<MobileOptOutRow[]>([]);
  const [reasonLabels, setReasonLabels] = useState<Record<string, string>>({});
  const [methodLabels, setMethodLabels] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [scopeFacet, setScopeFacet] = useState<ScopeFacet>('all');
  const [selected, setSelected] = useState<MobileOptOutRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('eds_opt_out')
        .select(`
          opt_out_id, recipient_id, list_id, reason_code, method_code,
          opted_out_at, reinstated_at, notes,
          eds_recipient!left ( email_address ),
          eds_recipient_list!left ( internal_code, eds_recipient_list_tl!left ( list_name, language_id ) )
        `)
        .order('opted_out_at', { ascending: false })
        .limit(500);
      if (e) throw e;

      type ListTl = TlTextRow & { list_name?: string };
      type RawRow = Omit<MobileOptOutRow, 'email_address' | 'list_name'> & {
        eds_recipient?: { email_address?: string } | null;
        eds_recipient_list?: { internal_code?: string; eds_recipient_list_tl?: ListTl[] } | null;
      };
      const flat: MobileOptOutRow[] = ((data ?? []) as RawRow[]).map((r) => {
        const ltls = (r.eds_recipient_list?.eds_recipient_list_tl ?? []).map((tl) => ({
          language_id: tl.language_id,
          display_text: tl.list_name ?? '',
        }));
        return {
          opt_out_id: r.opt_out_id,
          recipient_id: r.recipient_id,
          list_id: r.list_id,
          reason_code: r.reason_code,
          method_code: r.method_code,
          opted_out_at: r.opted_out_at,
          reinstated_at: r.reinstated_at,
          notes: r.notes,
          email_address: r.eds_recipient?.email_address ?? '',
          list_name: r.list_id
            ? (resolveTl(ltls, activeLocale) || r.eds_recipient_list?.internal_code || r.list_id)
            : null,
        };
      });
      setRows(flat);

      // §17.B4: resolve reason / method codes to localized names, never the raw code.
      const [{ data: reasonTl }, { data: methodTl }] = await Promise.all([
        apiDb.from('eds_opt_out_reason_tl').select('reason_code, language_id, display_text'),
        apiDb.from('eds_opt_out_method_tl').select('method_code, language_id, display_text'),
      ]);
      setReasonLabels(
        buildLabelMap(
          ((reasonTl ?? []) as { reason_code: string; language_id: string; display_text: string }[]).map((r) => ({
            code: r.reason_code, language_id: r.language_id, display_text: r.display_text,
          })),
          activeLocale,
        ),
      );
      setMethodLabels(
        buildLabelMap(
          ((methodTl ?? []) as { method_code: string; language_id: string; display_text: string }[]).map((r) => ({
            code: r.method_code, language_id: r.language_id, display_text: r.display_text,
          })),
          activeLocale,
        ),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const reasonLabel = useCallback((code: string) => reasonLabels[code] ?? code, [reasonLabels]);
  const methodLabel = (code: string) => methodLabels[code] ?? code;
  const scopeText = (r: MobileOptOutRow) =>
    r.list_id ? (r.list_name ?? t('mobile.eds_opt_out.scope_list', 'List')) : t('mobile.eds_opt_out.scope_global', 'Global');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (scopeFacet === 'global' && r.list_id) return false;
      if (scopeFacet === 'list' && !r.list_id) return false;
      if (!q) return true;
      return (
        r.email_address.toLowerCase().includes(q) ||
        (r.list_name ?? '').toLowerCase().includes(q) ||
        reasonLabel(r.reason_code).toLowerCase().includes(q)
      );
    });
  }, [rows, filter, scopeFacet, reasonLabel]);

  const facets: { key: ScopeFacet; label: string }[] = [
    { key: 'all', label: t('mobile.eds_opt_out.all', 'All') },
    { key: 'global', label: t('mobile.eds_opt_out.global', 'Global') },
    { key: 'list', label: t('mobile.eds_opt_out.list', 'List-scoped') },
  ];

  const stateLabel = (r: MobileOptOutRow) =>
    r.reinstated_at
      ? t('mobile.eds_opt_out.reinstated', 'Reinstated')
      : t('mobile.eds_opt_out.opted_out', 'Opted out');

  // §2: route the reinstate through the Common Routine (forensic stamp + audit).
  const reinstate = async (r: MobileOptOutRow) => {
    if (r.reinstated_at) return;
    setActionBusy(true);
    setError(null);
    const { error: e } = await routedUpdate(session, 'eds_opt_out', 'opt_out_id', r.opt_out_id, {
      reinstated_at: new Date().toISOString(),
      reinstated_by: getForensicActorId(session),
    });
    if (e) { setActionBusy(false); setError(e.message); return; }
    // A global opt-out also clears the recipient-level flag (mirrors desktop).
    if (!r.list_id) {
      const { error: e2 } = await routedUpdate(session, 'eds_recipient', 'recipient_id', r.recipient_id, {
        is_globally_opted_out: 'N',
      });
      if (e2) { setActionBusy(false); setError(e2.message); return; }
    }
    setActionBusy(false);
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.eds_opt_out.title', 'Opt-Outs')}
        description={t('mobile.eds_opt_out.description', 'Recipients who unsubscribed, globally or from a specific list.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.eds_opt_out.search', 'Search opt-outs')}
          aria-label={t('mobile.eds_opt_out.search', 'Search opt-outs')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => scopeFacet === v}
        onSelect={(v) => setScopeFacet(v as ScopeFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.eds_opt_out.none', 'No opt-outs match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.opt_out_id}
              lead={r.reinstated_at ? <MailCheck /> : <MailX />}
              leadTone={r.reinstated_at ? 'green' : 'red'}
              title={r.email_address}
              subtitle={`${scopeText(r)} · ${reasonLabel(r.reason_code)}`}
              trailing={<MobileStatusPill tone={r.reinstated_at ? 'ok' : 'no'}>{stateLabel(r)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.eds_opt_out.detail', 'Opt-out detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{selected.reinstated_at ? <MailCheck size={20} /> : <MailX size={20} />}</span>
              <span>
                <div className="m-nm">{selected.email_address}</div>
                <div className="m-em">{scopeText(selected)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.eds_opt_out.status', 'Status')}
                value={<MobileStatusPill tone={selected.reinstated_at ? 'ok' : 'no'}>{stateLabel(selected)}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.eds_opt_out.scope', 'Scope')}
                value={<MobileStatusPill tone={selected.list_id ? 'info' : 'no'}>{scopeText(selected)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.eds_opt_out.reason', 'Reason')} value={reasonLabel(selected.reason_code)} />
              <MobileDetailRow
                label={t('mobile.eds_opt_out.method', 'Method')}
                value={<MobileStatusPill tone={PILL_TONE_KEY[METHOD_PILL[selected.method_code] ?? 'm-p-wait']}>{methodLabel(selected.method_code)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.eds_opt_out.opted_out_at', 'Opted out')} value={formatDateTime(selected.opted_out_at, activeLocale)} />
              <MobileDetailRow label={t('mobile.eds_opt_out.reinstated_at', 'Reinstated')} value={selected.reinstated_at ? formatDate(selected.reinstated_at, activeLocale) : '—'} />
              {selected.notes && (
                <MobileDetailRow label={t('mobile.eds_opt_out.notes', 'Notes')} value={selected.notes} />
              )}
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren session={session} groups={[
                  { key: 'list', title: t('mobile.eds_opt_out.related.list', 'List'),
                  items: selected.list_id && selected.list_name ? [{ id: selected.list_id, label: selected.list_name }] : [] },
                ]} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy || !!selected.reinstated_at}
                title={selected.reinstated_at ? t('mobile.eds_opt_out.already_reinstated', 'Already reinstated') : undefined}
                onClick={canChange ? (() => void reinstate(selected)) : undefined}
              >
                <RotateCcw size={16} />
                {t('mobile.eds_opt_out.reinstate', 'Reinstate')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
